import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { profileAPI } from '../../utils/api';
import useUserStore from '../../store/userStore';
import { mergeGoalIntoProfile } from './onboardingFlowLogic';
import { toast } from 'sonner';
import { Check, ChevronLeft, Scale, Target } from 'lucide-react';
import useKeyboardAware from '../../hooks/useKeyboardAware';
import { goalSelectionNextRoute, shouldBlockOnboardingOnGoalSaveFailure } from './onboardingFlowLogic';

// Goals in the requested order (removed "Tone Up")
const goals = [
  { value: 'Lose Weight', label: 'Lose Weight', description: 'Lose body fat percentage' },
  { value: 'Improve Health', label: 'Improve Wellness', description: 'Focus on overall wellness' },
  { value: 'Build Muscle', label: 'Build Muscle', description: 'Increase muscle mass' },
  { value: 'Body Recomposition', label: 'Body Recomposition', description: 'Lose fat while building muscle' },
  { value: 'Maintain', label: 'Maintain', description: 'Keep current physique' },
  { value: 'Gain Weight', label: 'Gain Weight', description: 'Gradual weight gain' },
];

const GoalSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setProfile, setOnboardingData, profile, user } = useUserStore();
  useKeyboardAware();

  // Manual-macros onboarding routes here first (MacroSetupChoice -> GoalSelection
  // [manual mode] -> MacroQuiz [manual mode] -> ManualMacros -> Allergens) so
  // a goal is always captured before macros, instead of skipping straight to
  // manual entry with no goal (which previously let a profile end up with
  // real targets but no goal). Both modes now go through the same MacroQuiz
  // body/profile questionnaire — goalSelectionNextRoute() below carries the
  // manual-mode tag forward in location.state so MacroQuiz knows never to
  // call backend target generation or overwrite the macros the user is
  // about to enter themselves in ManualMacros.

  // Derive auth state from user presence
  const isAuthenticated = !!user;
  const isDemoMode = user?.user_id?.startsWith('demo_');
  const [selectedGoal, setSelectedGoal] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Lose Weight specific states
  const [step, setStep] = useState('goal'); // 'goal' | 'hasGoalWeight' | 'goalWeight'
  const [hasGoalWeight, setHasGoalWeight] = useState(null); // true | false | null
  const [goalWeight, setGoalWeight] = useState('');

  const handleGoalSelect = (goalValue) => {
    setSelectedGoal(goalValue);
  };

  const handleContinue = async () => {
    if (!selectedGoal) {
      toast.error('Please select a goal');
      return;
    }

    // If "Lose Weight" is selected, go to the goal weight question
    if (selectedGoal === 'Lose Weight' && step === 'goal') {
      setStep('hasGoalWeight');
      return;
    }

    // Proceed to save and navigate
    await saveAndNavigate();
  };

  const handleHasGoalWeightAnswer = (answer) => {
    setHasGoalWeight(answer);
    if (answer) {
      setStep('goalWeight');
    } else {
      // No goal weight - proceed directly to quiz
      saveAndNavigate(null, false);
    }
  };

  const handleGoalWeightSubmit = () => {
    const goalWeightNum = parseFloat(goalWeight);
    const currentWeight = profile?.weight_lbs || profile?.weight || 0;
    
    if (!goalWeightNum || goalWeightNum <= 0) {
      toast.error('Please enter a valid goal weight');
      return;
    }
    
    if (currentWeight && goalWeightNum >= currentWeight) {
      toast.error('Goal weight must be lower than your current weight for this goal');
      return;
    }
    
    // Save goal weight and continue - timeline will be shown AFTER onboarding
    saveAndNavigate(goalWeightNum, true);
  };

  const saveAndNavigate = async (goalWeightValue = null, hasGoal = false) => {
    setLoading(true);
    // Map "Lose Weight" to "Lose Body Fat" for backend compatibility
    const backendGoal = selectedGoal === 'Lose Weight' ? 'Lose Body Fat' : selectedGoal;

    const goalData = {
      goal: backendGoal,
      display_goal: selectedGoal, // Store original for UI display
      goal_weight_lbs: goalWeightValue,
      has_goal_weight: hasGoal,
    };

    try {
      // If user is logged in, save to backend
      if (isAuthenticated && !isDemoMode) {
        await profileAPI.setGoal(backendGoal);
        setProfile(mergeGoalIntoProfile(useUserStore.getState().profile, goalData));
      } else {
        setOnboardingData(goalData);
        setProfile(mergeGoalIntoProfile(useUserStore.getState().profile, goalData));
      }

      // Manual mode: the user is about to enter their own macros — never
      // route through MacroQuiz (which would calculate/replace them).
      const nextRoute = goalSelectionNextRoute(location.state, backendGoal, goalData);
      navigate(nextRoute.path, { state: nextRoute.state });
    } catch (error) {
      if (shouldBlockOnboardingOnGoalSaveFailure(isAuthenticated, isDemoMode)) {
        // A real account's goal must actually be persisted — Smart Fill
        // later fails closed on a missing goal, so silently continuing here
        // would just move the failure downstream to a confusing
        // planning-time error instead of letting the user retry now (see
        // AGENTS.md pre-launch audit nutrition-readiness follow-up). Stay on
        // this screen; do not navigate onward.
        toast.error("Couldn't save your goal. Please check your connection and try again.");
      } else {
        // Demo/local-only: no backend profile to save to — continue with
        // local state as before.
        setOnboardingData(goalData);
        setProfile(mergeGoalIntoProfile(useUserStore.getState().profile, goalData));
        const fallbackRoute = goalSelectionNextRoute(location.state, backendGoal);
        navigate(fallbackRoute.path, { state: fallbackRoute.state });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'hasGoalWeight') {
      setStep('goal');
      setHasGoalWeight(null);
    } else if (step === 'goalWeight') {
      setStep('hasGoalWeight');
      setGoalWeight('');
    }
  };

  // Render Goal Selection Screen
  const renderGoalSelection = () => (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <button
        onClick={() => navigate('/onboarding/setup')}
        data-testid="goal-back-btn"
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft size={18} /> Back
      </button>

      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
          <Target size={24} className="text-accent" />
        </div>
        <h1 className="font-heading font-bold text-2xl" data-testid="goal-selection-title">What's your goal?</h1>
        <p className="text-muted-foreground text-sm">Choose the goal that aligns with your fitness journey</p>
      </div>

      <div className="space-y-3">
        {goals.map((goal) => (
          <motion.div
            key={goal.value}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleGoalSelect(goal.value)}
            data-testid={`goal-${goal.value.toLowerCase().replace(/\s+/g, '-')}`}
            className={`bg-card border rounded-xl p-4 cursor-pointer transition-all ${
              selectedGoal === goal.value
                ? 'border-accent bg-accent/5'
                : 'border-border/40 hover:border-accent/50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-base">{goal.label}</h3>
                <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>
              </div>
              {selectedGoal === goal.value && (
                <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                  <Check size={14} className="text-white" />
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      <Button
        onClick={handleContinue}
        data-testid="continue-goal-btn"
        disabled={!selectedGoal || loading}
        className="w-full h-12 rounded-full"
      >
        {loading ? 'Saving...' : 'Continue'}
      </Button>
    </motion.div>
  );

  // Render "Do you have a goal weight?" Screen
  const renderHasGoalWeightQuestion = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      <button
        onClick={handleBack}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft size={18} /> Back
      </button>

      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
          <Target size={24} className="text-accent" />
        </div>
        <h1 className="font-heading font-bold text-2xl">Do you have a goal weight in mind?</h1>
        <p className="text-muted-foreground text-sm">We can estimate how long it might take to reach it</p>
      </div>

      <div className="space-y-3">
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleHasGoalWeightAnswer(true)}
          data-testid="has-goal-weight-yes"
          className="bg-card border border-border/40 hover:border-accent/50 rounded-xl p-4 cursor-pointer transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Check size={20} className="text-accent" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">Yes - I have a goal</h3>
              <p className="text-sm text-muted-foreground">I know my target weight</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleHasGoalWeightAnswer(false)}
          data-testid="has-goal-weight-no"
          className="bg-card border border-border/40 hover:border-accent/50 rounded-xl p-4 cursor-pointer transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
              <Scale size={20} className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">No - just help me lose weight</h3>
              <p className="text-sm text-muted-foreground">I'll track my progress as I go</p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  // Render Goal Weight Input Screen
  const renderGoalWeightInput = () => (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      className="space-y-6"
    >
      <button
        onClick={handleBack}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft size={18} /> Back
      </button>

      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
          <Scale size={24} className="text-accent" />
        </div>
        <h1 className="font-heading font-bold text-2xl">What's your goal weight?</h1>
        <p className="text-muted-foreground text-sm">Enter your target weight in pounds</p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <Input
            type="number"
            inputMode="decimal"
            enterKeyHint="done"
            value={goalWeight}
            onChange={(e) => setGoalWeight(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
                handleGoalWeightSubmit();
              }
            }}
            placeholder="Enter goal weight"
            data-testid="goal-weight-input"
            className="h-14 text-center text-xl font-semibold pr-12"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
            lbs
          </span>
        </div>

        {profile?.weight_lbs && (
          <p className="text-sm text-muted-foreground text-center">
            Current weight: {profile.weight_lbs} lbs
          </p>
        )}
      </div>

      <Button
        onClick={handleGoalWeightSubmit}
        data-testid="continue-goal-weight-btn"
        disabled={!goalWeight || loading}
        className="w-full h-12 rounded-full"
      >
        {loading ? 'Saving...' : 'Continue'}
      </Button>
    </motion.div>
  );

  return (
    <div
      className="min-h-screen header-safe-top px-6 pb-24"
      onClick={(e) => {
        // Tap outside any input to dismiss the iOS keyboard. Helps the goal
        // weight screen feel less "stuck" once a number is entered.
        if (
          e.target instanceof HTMLElement &&
          !e.target.closest('input, textarea, button, [role="button"], [data-testid]')
        ) {
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }
      }}
    >
      <div className="max-w-md mx-auto space-y-8">
        <AnimatePresence mode="wait">
          {step === 'goal' && renderGoalSelection()}
          {step === 'hasGoalWeight' && renderHasGoalWeightQuestion()}
          {step === 'goalWeight' && renderGoalWeightInput()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GoalSelection;
